/**
 * Console state, held in the URL.
 *
 * Which source, which view, which issue and which year the reader is looking at
 * all belong in the address bar: this is a screen people send each other
 * ("look at the PDF chain's balance-sheet finding"), and state that lives only
 * in a component cannot be sent.
 *
 * Two navigation rules:
 *   - Filter changes REPLACE the history entry. Ticking four severity chips
 *     should not cost four presses of Back to leave the page.
 *   - Source, view and issue changes PUSH. Back should undo a drill-down.
 *
 * Every value degrades silently when it is not valid for the data actually
 * loaded — a stale link naming a source this company no longer has falls back
 * to the worst-scoring chain rather than rendering an error.
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { IssueSeverity, ValidationChain } from '../../../../types/dataQuality';
import { chainSource, orderChains, worstChainIndex } from '../dqSelectors';
import type { IssueFilter } from '../dqSelectors';
import type { RuleFamily } from '../dqRules';

export type ConsoleView = 'summary' | 'findings' | 'fields' | 'compare';

const PARAM = {
  source: 'dqSource',
  view: 'dqView',
  issue: 'dqIssue',
  year: 'dqYear',
  field: 'dqField',
  severity: 'dqSev',
  family: 'dqFam',
  // The Findings facet (multi-select), distinct from `year` — which is the
  // single year the Fields view is showing.
  years: 'dqYears',
  verdicts: 'dqVerdict',
} as const;

/** Comma-separated list params, tolerant of empties and stray whitespace. */
function readList(value: string | null): string[] {
  if (!value) return [];
  return value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

export interface DataQualityConsoleState {
  /** The chain being viewed. Never null once chains exist. */
  chain: ValidationChain | null;
  /** "API" | "PDF" — the value the detail endpoints want. */
  source: string;
  setSource: (source: string) => void;

  view: ConsoleView;
  setView: (view: ConsoleView) => void;

  /** The issue code whose dossier is open, if any. */
  selectedCode: string | null;
  setSelectedCode: (code: string | null) => void;

  /** The year the Fields view is showing. */
  year: number | null;
  setYear: (year: number | null) => void;

  /** A canonical field name to scroll to and highlight. */
  field: string | null;
  setField: (field: string | null) => void;

  filter: IssueFilter;
  toggleSeverity: (severity: IssueSeverity) => void;
  toggleFamily: (family: RuleFamily) => void;
  toggleYear: (year: number) => void;
  toggleVerdict: (verdict: string) => void;
  clearFilter: () => void;

  /** Chart/strip → Findings, filtered to one year, in a single navigation. */
  drillIntoYear: (year: number) => void;
  /** Finding's field chip → Fields view, scrolled to that field. */
  showField: (canonical: string) => void;
}

export function useDataQualityConsole(
  chains: ValidationChain[] | null | undefined,
  availableViews: ConsoleView[]
): DataQualityConsoleState {
  const [params, setParams] = useSearchParams();

  const ordered = useMemo(() => orderChains(chains ?? []), [chains]);

  // An explicit choice wins; otherwise the WORST-scoring chain, so a bad source
  // is never hidden behind a click. Recomputed from the data rather than stored,
  // so a refetch that changes the scores re-points the default.
  const requestedSource = (params.get(PARAM.source) || '').toUpperCase();
  const chain =
    ordered.find((c) => chainSource(c) === requestedSource) ??
    ordered[worstChainIndex(ordered)] ??
    null;

  const requestedView = params.get(PARAM.view) as ConsoleView | null;
  const view =
    requestedView && availableViews.includes(requestedView) ? requestedView : availableViews[0];

  const rawYear = Number(params.get(PARAM.year));
  const year = Number.isFinite(rawYear) && rawYear > 0 ? rawYear : null;

  const filter: IssueFilter = useMemo(
    () => ({
      severity: readList(params.get(PARAM.severity)) as IssueSeverity[],
      family: readList(params.get(PARAM.family)) as RuleFamily[],
      years: readList(params.get(PARAM.years))
        .map(Number)
        .filter((y) => Number.isFinite(y)),
      verdicts: readList(params.get(PARAM.verdicts)),
    }),
    [params]
  );

  const update = useCallback(
    (changes: Record<string, string | null>, replace: boolean) => {
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          for (const [key, value] of Object.entries(changes)) {
            if (value == null || value === '') next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace }
      );
    },
    [setParams]
  );

  const toggleInList = useCallback(
    (key: string, value: string) => {
      const current = readList(params.get(key));
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      update({ [key]: next.join(',') }, true);
    },
    [params, update]
  );

  return {
    chain,
    source: chain ? chainSource(chain) : '',
    /**
     * Switching source resets everything scoped to a chain: the open dossier,
     * the highlighted field, the selected year AND every filter facet.
     *
     * The facets matter most. A finding code and a fiscal year obviously belong
     * to the chain they were opened on, but so does a year facet — filter to
     * FY2019 on the PDF chain and switch to API, and if API raised nothing that
     * year the chip is not even rendered, leaving the reader staring at "no
     * findings match this filter" with no visible filter to explain it.
     */
    setSource: (source) =>
      update(
        {
          [PARAM.source]: source,
          [PARAM.issue]: null,
          [PARAM.field]: null,
          [PARAM.year]: null,
          [PARAM.severity]: null,
          [PARAM.family]: null,
          [PARAM.years]: null,
          [PARAM.verdicts]: null,
        },
        false
      ),

    view,
    setView: (next) => update({ [PARAM.view]: next }, false),

    selectedCode: params.get(PARAM.issue),
    // `replace`, not push: arrowing down a list of fifteen findings must not
    // cost fifteen presses of Back to leave the page.
    setSelectedCode: (code) => update({ [PARAM.issue]: code }, true),

    year,
    setYear: (next) => update({ [PARAM.year]: next == null ? null : String(next) }, true),

    field: params.get(PARAM.field),
    setField: (next) => update({ [PARAM.field]: next }, true),

    filter,
    toggleSeverity: (severity) => toggleInList(PARAM.severity, severity),
    toggleFamily: (family) => toggleInList(PARAM.family, family),
    toggleYear: (y) => toggleInList(PARAM.years, String(y)),
    toggleVerdict: (verdict) => toggleInList(PARAM.verdicts, verdict),
    clearFilter: () =>
      update(
        {
          [PARAM.severity]: null,
          [PARAM.family]: null,
          [PARAM.years]: null,
          [PARAM.verdicts]: null,
        },
        true
      ),

    /**
     * Drill from a year on a chart into that year's findings — in ONE update.
     *
     * This has to be a single call. `useSearchParams`' setter closes over the
     * params from the render that produced it, so three sequential calls each
     * read the same stale base and the last one silently wins: the previous
     * implementation set the year filter but never actually switched the view,
     * so clicking a bar looked like it did nothing while arming an invisible
     * filter.
     */
    drillIntoYear: (y) =>
      update(
        {
          [PARAM.view]: 'findings',
          [PARAM.year]: String(y),
          [PARAM.years]: String(y),
          // The year the reader chose replaces any earlier year selection
          // rather than adding to it — they clicked one bar, not two.
          [PARAM.issue]: null,
        },
        false
      ),

    /** Jump to one field in the Fields view. Also a single update. */
    showField: (canonical) =>
      update({ [PARAM.view]: 'fields', [PARAM.field]: canonical }, false),
  };
}

/** Exported for tests and for deep links built outside the console. */
export const CONSOLE_PARAMS = PARAM;
